import React, {useState, useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import * as XLSX from "xlsx";
import Fuse from "fuse.js";
import { parse, parseISO, differenceInDays, isValid } from "date-fns";
import localforage from 'localforage';

const headerNames = [
    "Select",
    "Location",
    "Caregiver's Name",
    "Childname",
    "IP Group",
    "Sex",
    "Birthdate",
    "Date Measured",
    "Weight (kg)",
    "Height (cm)",
    "Age (mos.)",
    "WA",
    "HA",
    "WH"
];

// Reusing the button style from other components for consistency
const buttonStyle = {
  padding: "10px 20px",
  backgroundColor: "#007bff",
  color: "#fff",
  border: "none",
  borderRadius: "5px",
  fontWeight: "bold",
  cursor: "pointer",
  marginTop: "10px",
  transition: 'background-color 0.3s ease',
};

// New style for dropdowns
const dropdownStyle = {
  padding: '8px',
  borderRadius: '5px',
  border: '1px solid #ccc',
  marginRight: '10px',
  fontSize: '16px',
};

const CircularLoader = () => (
  <div
    style={{
      border: "3px solid #f3f3f3",   // Light gray background
      borderTop: "3px solid white",  // White top for animation
      borderRadius: "50%",
      width: "16px",
      height: "16px",
      animation: "spin 1s linear infinite",
    }}
  />
);

function isDateWithinTolerance(date1, date2, toleranceDays = 1) {
  let d1, d2;

  const isMMMddyyyy = (dateStr) =>
    typeof dateStr === "string" && /^[a-zA-Z]{3}-\d{2}-\d{4}$/.test(dateStr);

  // Parse date1
  if (isMMMddyyyy(date1)) {
    d1 = parse(date1, "MMM-dd-yyyy", new Date());
  } else {
    d1 = parseISO(date1);
  }

  // Parse date2
  if (isMMMddyyyy(date2)) {
    d2 = parse(date2, "MMM-dd-yyyy", new Date());
  } else {
    d2 = parseISO(date2);
  }

  // Validate
  if (!isValid(d1) || !isValid(d2)) {
    console.warn("Invalid date(s):", {
      date1,
      date2,
      d1,
      d2,
    });
    return false;
  }

  const diff = Math.abs(differenceInDays(d1, d2));
  return diff <= toleranceDays;
}

const options = {
  includeScore: true,
  threshold: 0.25,
  ignoreLocation: true,
  keys: ["combinedName"],
};

const OPTMerge = () => {
    let baseUrl = `${process.env.REACT_APP_API_URL}/api/locations/api/optdata`
    const navigate = useNavigate()
    useEffect(() => {
        const userRole = parseInt(localStorage.getItem("userRole"), 10);
        // Redirect if the user role is not 1
        // if (userRole !== 1) {
        //     navigate('/');
        // }
    }, [navigate]);

    // Monitor the state of the merging process
    const [loading, setLoading] = useState(false);

    // Display the outcome of the merge
    const [showPopup, setShowPopup] = useState(false);
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [mergeStatus, setMergeStatus] = useState({});

    // Rows of data from the uploaded sheet
    const [data, setData] = useState([]);
    const [fetchedOPTData, setFetchedOPTData] = useState({});

    const [fullMatchData, setFullMatchData] = useState([]);
    const [partialMatchData, setPartialMatchData] = useState([]);
    const [noMatchData, setNoMatchData] = useState([]);


    // State for the centralized search functionality
    const [searchQuery, setSearchQuery] = useState(''); 
    const [isSearchApplied, setIsSearchApplied] = useState(false);
    const [appliedLocationData, setAppliedLocationData] = useState({});

    // New states for dropdown selections and options
    const [provinces, setProvinces] = useState([]);
    const [selectedProvince, setSelectedProvince] = useState('');
    const [municipals, setMunicipals] = useState([]);
    const [selectedMunicipal, setSelectedMunicipal] = useState('');
    const [barangays, setBarangays] = useState([]);
    const [selectedBarangay, setSelectedBarangay] = useState('');

    const [file, setFile] = useState(null);
    const [error, setError] = useState("");

    // selectedData
    const selectedPartialMatches = {}
    const selectedFullMatches = useRef([]);
    const selectedNoMatches = useRef([]);

    const fetchOPTData = async () => {
        try {
            const location = `${appliedLocationData.provinceName}_${appliedLocationData.municipalName}_${appliedLocationData.barangayName}`
            const apiUrl = `${baseUrl}?location=${encodeURIComponent(location)}`;
            const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'x-api-key': process.env.REACT_APP_API_KEY
            }
            });
            const data = await response.json();
            if (Array.isArray(data) && data.length > 0) {
                setFetchedOPTData(data);
                return data;
            } else {
                console.warn(`API returned an empty array or non-array data. Data received:`, data);
                // Default empty fetchedData if no data
            }
      } catch (error) {
        console.error(`Error fetching OPT data:`, error);
        // Default empty fetchedData on error
      }
    }

    const postOPTData = async (data) => {
        const location = `${appliedLocationData.provinceName}_${appliedLocationData.municipalName}_${appliedLocationData.barangayName}`
        const apiUrl = `${baseUrl}?location=${encodeURIComponent(location)}`;
        const response = await fetch(apiUrl,
            {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "x-api-key": process.env.REACT_APP_API_KEY,
            },
            body: JSON.stringify(data),
            }
        );

        const result = await response.json();

        if (!response.ok) {
            // Log the detailed error from the server if available
            console.error("Server error response:", result);
            throw new Error(result.message || `Failed to save data. Status: ${response.status}`);
        }

        return(result.status)
    }

    const handleCheckboxChange = (array, rowIndex, checked) => {
        if (checked) {
            if (!array.includes(rowIndex)) {
            array.push(rowIndex); // add
            }
        } else {
            const index = array.indexOf(rowIndex);
            if (index > -1) {
            array.splice(index, 1); // remove in place
            }
        }
    };


    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];

        if (selectedFile) {
        if (selectedFile.name.endsWith(".xlsx")) {
            setFile(selectedFile);
            setError("");
        } else {
            setError("Only .xlsx files are allowed");
            setFile(null);
        }
        }
    };

    const handleFile = () => {
        setLoading(true);
        const reader = new FileReader();
        const fullMatch = [];
        const partialMatch = [];
        const noMatch = [];

        reader.onload = async (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });

            // Get the first sheet
            const sheetName = "Nut_StatusTool";
            const sheet = workbook.Sheets[sheetName];

            // Convert sheet to JSON (array of arrays)
            const allRows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false });
            const slicedRows = allRows.slice(9).map(row => row.slice(0, 14));

            // Slice from row 10 (index 9, since it's 0-based) and take columns A–N (0–13)
            let filteredRows = [];
            for (let row of slicedRows) {
                // Check if the row is completely empty
                const isEmpty = row.every((cell, index) => index === 0 || cell === null || cell === undefined || cell === "");
                
                if (isEmpty) {
                    break; // stop reading further
                }

                filteredRows.push(row);
            }
            
            let dataFetched;
            try {
                dataFetched = await fetchOPTData();
                // Check if data is empty or null
                // if (!dataFetched || (Array.isArray(dataFetched) && dataFetched.length === 0)) {
                //     console.log("No data found.");
                //     setErrorMessage("No data found!");
                //     setShowErrorPopup(true);
                //     setLoading(false);
                // }
            } catch (error) {
                console.error("Error fetching data:", error);
                setErrorMessage("There is an error fetching data.");
                setShowErrorPopup(true)
                setLoading(false);
                return
            }

            setData(filteredRows);

            if (dataFetched && Object.keys(dataFetched).length !== 0) {
                const normalizedRecords = dataFetched
                    .filter(r => r && r.headname && r.childname)  // filter out any incomplete records
                    .map(r => ({
                        parent: r.headname,
                        child: r.childname,
                        birthdate: r.birthdate,
                        combinedName: `${r.headname}${r.childname}`,
                        original: r
                }));

                const fuse = new Fuse(normalizedRecords, options);

                for (let newRecord of filteredRows) {
                    // search for parent + child names
                    const parent = newRecord[2];
                    const child = newRecord[3];
                    const searchString = `${parent}${child}`;

                    // remove white spaces from the names
                    const results = fuse.search(searchString);


                    if (results.length > 0) {
                        const bestMatch = results[0]; // Fuse results sorted by score

                        const matchedRecords = results
                            .filter(r => isDateWithinTolerance(r.item.birthdate, newRecord[6], 1))
                            .map(r => {
                                const obj = { ...r.item.original };

                                // Helper function for formatting
                                const formatDate = (dateStr) =>
                                    new Date(dateStr)
                                        .toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })
                                        .replace(/,/g, "")        // remove commas
                                        .replace(/\s+/g, "-"); 

                                // Reformat fields if they exist
                                if (obj.birthdate) obj.birthdate = formatDate(obj.birthdate);
                                if (obj.date_measured) obj.date_measured = formatDate(obj.date_measured);

                                return Object.values(obj); // return as array of values
                            });

                        if (matchedRecords.length > 0) {
                            
                            const isExactMatch = bestMatch.score < 1e-3;
                            

                            if (isExactMatch) { 
                                fullMatch.push(newRecord);
                            } else if (bestMatch.score > 0 && bestMatch.score <= 0.25) {
                                partialMatch.push([newRecord, ...matchedRecords]);
                            } else {
                                noMatch.push(newRecord);
                            }
                        } else {
                            noMatch.push(newRecord);
                        }
                    } else {
                        noMatch.push(newRecord);
                    }
                }
                // Save data states
                setFullMatchData(fullMatch);
                setPartialMatchData(partialMatch);
                setNoMatchData(noMatch);
            } else {
                setFullMatchData(fullMatch);
                setPartialMatchData(partialMatch);
                setNoMatchData(filteredRows);
            }

            // Stop loading
            setLoading(false);
        };

        reader.onerror = () => {
            console.error("Error reading file");
            setLoading(false); // 🔹 stop loading even if error
        };

        reader.readAsArrayBuffer(file);
    };

    const handleItemChange = (rowIndex, colIndex, newValue) => {
        const updatedData = [...data];
        updatedData[rowIndex][colIndex] = newValue;
        setData(updatedData);
    };

    const ExpandableRow = ({ data, type }) => {
        const [expanded, setExpanded] = useState(false);


        return (
            <div style={{ borderBottom: "1px solid #ccc", padding: "8px" }}>
            {/* Row Header */}
            <div
                onClick={() => setExpanded(!expanded)}
                style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                fontWeight: "bold",
                }}
            >
                <span style={{color: type === 'Full Matches' ? "green" : type === 'Partial Matches' ? 'orange' : 'red', paddingLeft: "12px"}}>
                    Data with {type} ({data.length} matches)
                </span>
                <span>{expanded ? "▲" : "▼"}</span>
            </div>

            {/* Expanded Content */}
            {expanded && (
                <div style={{
                    marginTop: "6px",
                    paddingLeft: "12px",
                    color: "#444",
                    display: "grid",
                    gridTemplateColumns: "repeat(14, 1fr)", // 3 equal columns
                    columnGap: "10px", // gap-x
                    rowGap: "10px",     // gap-y
                    overflowX: "auto",
                    whiteSpace: "nowrap"
                }}>
                    {headerNames.map((name, index) => {
                        return <span key={index} style={{fontWeight: "bold", textAlign: "center"}}>
                            {name}
                        </span>
                    })}
                    {data.map((row, rowIndex) => (
                        row.map((item, colIndex) => {
                            if (colIndex === 0) {
                                return <input
                                    key={`${rowIndex}${colIndex}`}
                                    defaultChecked={type === "Full Matches" || type === "No Matches"} 
                                    onChange={(e) => {
                                        if (type === "Full Matches") {
                                            handleCheckboxChange(selectedFullMatches.current, rowIndex, e.target.checked)
                                        } else if (type === "No Matches") {
                                            handleCheckboxChange(selectedNoMatches.current, rowIndex, e.target.checked)
                                        }
                                    }}
                                    type="checkbox"
                                    style={{
                                        width: "16px",
                                        height: "16px",
                                        cursor: "pointer",
                                        margin: "auto",
                                        accentColor: "#3b82f6",
                                    }}
                                    />
                            }
                            return <span
                                key={`${rowIndex}${colIndex}`}
                                style={{
                                    border: "1px solid gray",  // width + style + color
                                    padding: "4px",            // optional for spacing
                                    borderRadius: "4px"        // optional rounded corners
                                }}
                                >
                                {item}
                            </span>
                        })
                    ))}
                    {data.length === 0 && <span style={{gridColumn: "span 13", textAlign: "center", padding: "10px"}}>No Entries</span>}
                </div>
            )}
            </div>
        );
    };

    const FullMatchDataRows = ({ data, type }) => {
        return (
            <div style={{ width: "95vw", margin: "10px auto", border: "1px solid #ddd", borderRadius: "6px" }}>
                <ExpandableRow data={data} type={type} />
            </div>
        );
    };

    const Expandable = ({ data, index }) => {
        const [expanded, setExpanded] = useState(false);

        return (
            <div style={{ borderBottom: "1px solid #ccc", padding: "8px" }}>
            {/* Row Header */}
            <div
                onClick={() => setExpanded(!expanded)}
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                    fontWeight: "bold",
                }}
            >
                <span style={{color: "black", paddingLeft: "12px"}}>
                    {/* Display child name */}
                    {data[0][3]} 
                </span>
                <span>{expanded ? "▲" : "▼"}</span>
            </div>

            {/* Expanded Content */}
            {expanded && (
                <div>
                    <Table rows={data} rowsIndex={index}/>
                </div>
            )}
            </div>
        );
    };

    const PartialMatchExpandableRow = ({ data, type }) => {
        const [expanded, setExpanded] = useState(false);
        
        return (
            <div style={{ borderBottom: "1px solid #ccc", padding: "8px" }}>
            {/* Row Header */}
            <div
                onClick={() => setExpanded(!expanded)}
                style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                fontWeight: "bold",
                }}
            >
                <span style={{color: 'orange', paddingLeft: "12px"}}>
                    Data with {type} ({data.length} matches)
                </span>
                <span>{expanded ? "▲" : "▼"}</span>
            </div>

            {/* Expanded Content */}
            {expanded && (
                <div style={{
                    marginTop: "6px",
                    paddingLeft: "12px",
                    color: "#444",
                    overflowX: "auto",
                    whiteSpace: "nowrap"
                }}>
                    {data.map((row, rowIndex) => (
                        
                        <Expandable 
                            key={rowIndex} 
                            data={row} 
                            index={rowIndex} 
                        />
                        )
                        
                    )}
                    {data.length === 0 && <span style={{gridColumn: "span 13", textAlign: "center", padding: "10px"}}>No Entries</span>}
                </div>
            )}
            </div>
        );
    };

    const PartialMatchDataRows = ({data, type}) => {
        return (
            <div style={{ width: "95vw", margin: "10px auto", border: "1px solid #ddd", borderRadius: "6px" }}>
                <PartialMatchExpandableRow data={data} type={type}/>
            </div>
        );
    }

    function Table({ rows, rowsIndex }) {
        
        const newRecord = rows[0];

        return (
            <div style={{
                marginTop: "6px",
                paddingLeft: "12px",
                color: "#444",
                display: "grid",
                gridTemplateColumns: "repeat(14, 1fr)", // 3 equal columns
                columnGap: "10px", // gap-x
                rowGap: "10px",     // gap-y
                overflowX: "auto",
                whiteSpace: "nowrap"
            }}>
            {headerNames.map((name, index) => {
                return <span key={index} style={{fontWeight: "bold", textAlign: "center"}}>
                    {name}
                </span>
                })}
            {rows.map((row, rowIndex) => (
                row.map((item, colIndex) => {
                    if (colIndex === 0) {
                    return (
                        <input
                        key={colIndex}
                        type="radio"
                        name={`rowSelector-${rowsIndex}`}
                        onClick={() => {
                            selectedPartialMatches[rowsIndex] = rowIndex;
                        }}
                        readOnly
                        style={{
                            width: "16px",
                            height: "16px",
                            cursor: "pointer",
                            margin: "auto",
                            accentColor: "#3b82f6",
                        }}
                        />
                    );
                    }

                    return (
                    <input
                        key={colIndex}
                        type="text"
                        value={item}
                        // onChange={(e) =>
                        //   handleItemChange(rowIndex, colIndex, e.target.value)
                        // }
                        readOnly
                        style={{
                            backgroundColor: rowIndex === 0 ? "#f0f9ff" : "",
                            padding: "8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "14px",
                            borderColor: (colIndex !== 2 && colIndex !== 3 && colIndex !== 6) ? "" : (item !== newRecord[colIndex]) ? "red" : ""
                        }}
                    />
                    );
                })
            ))}
            {rows.length === 0 && <span style={{gridColumn: "span 13", textAlign: "center", padding: "10px"}}>No Entries</span>}
            </div>
        );
        }

    useEffect(() => {
        selectedFullMatches.current = fullMatchData.map((_, index) => index);
        selectedNoMatches.current = noMatchData.map((_, index) => index);

    }, [fullMatchData, noMatchData]);

    // Fetch provinces 
    useEffect(() => {
        const fetchProvinces = async () => {
            const cacheProvinceKey = 'location_province';
            try {
                const data = await localforage.getItem(cacheProvinceKey)
                setProvinces(data.data);
                setSelectedProvince({}); // Reset province when region changes
                setMunicipals([]);       // Clear municipals
                setSelectedMunicipal({}); // Reset municipal
                setBarangays([]);         // Clear barangays
                setSelectedBarangay({});  // Reset barangay
            } catch (error) {
                console.error('Error fetching provinces:', error);
            }
        };
        fetchProvinces();
    }, []);

    // Fetch municipals (cities-municipalities) when a province is selected
    useEffect(() => {
        if (selectedProvince) {
            const cacheMunicipalityKey = 'location_municipality';
            const fetchMunicipals = async () => {
                try {
                    const data = await localforage.getItem(cacheMunicipalityKey);
                    const filteredData = Array.isArray(data.data) && selectedProvince
                    ? data.data.find(item => item.province_id === Number(selectedProvince ? selectedProvince.province_id : 0))?.municipalities
                    : [];
                    setMunicipals(filteredData); // Using unfiltered data for simplicity
                    setSelectedMunicipal({}); // Reset municipal when province changes
                    setBarangays([]);         // Clear barangays
                    setSelectedBarangay({});  // Reset barangay
                } catch (error) {
                    console.error('Error fetching municipals:', error);
                }
            }
            fetchMunicipals();
        } else {
            setMunicipals([]);
            setSelectedMunicipal({});
            setBarangays([]);
            setSelectedBarangay({});
        }
    }, [selectedProvince, provinces]);

    // Fetch barangays when a municipal (or city) is selected
    useEffect(() => {
        if (selectedMunicipal) {
            const cacheBarangayKey = 'location_barangay';
            const fetchBarangays = async () => {
                const data = await localforage.getItem(cacheBarangayKey);
                const filteredData = Array.isArray(data.data) && selectedMunicipal
                ? data.data.find(item => item.municipality_id === Number(selectedMunicipal?.municipality_id || 0))?.barangays
                : [];
                setBarangays(filteredData);
                setSelectedBarangay({}); // Reset barangay when municipal changes
            };
            fetchBarangays();
        } else {
            setBarangays([]);
            setSelectedBarangay({});
        }
    }, [selectedMunicipal, municipals]);

    // Function to handle the centralized "Apply Search" button click
    const handleApplySearch = async () => {
        if (selectedBarangay?.barangay_name?.trim()) {
            setIsSearchApplied(true);
            setSearchQuery(selectedBarangay.barangay_name);

            const locationData = {
                regionName: '',
                provinceName: selectedProvince?.province_name || '',
                municipalName: selectedMunicipal?.municipality_name || '',
                barangayName: selectedBarangay?.barangay_name || '',
            };

            setAppliedLocationData(locationData);
        } else {
            alert('Please select a Barangay to apply search.');
            setIsSearchApplied(false);
            setSearchQuery('');
            setAppliedLocationData({});
        }
    };

    const handleMerge = async () => {
        const dataToInsert = [];
        const dataToUpdate = [];

        // stage rows of data to submit to the database
        // rows with partial matches
        for (let k of Object.keys(selectedPartialMatches)) {
            const i = selectedPartialMatches[k];
            const row = partialMatchData[k][i];
            if (i === 0) { // Insert as new entry
                dataToInsert.push(row);
            } else {
                const newRow = partialMatchData[k][0];
                // Update the date_measured, weight, height, age, WA, HA, and WH fields of row with
                // the corresponding fields of newRow
                for (let indexValue = 7; indexValue < 14; indexValue++) {
                    row[indexValue] = newRow[indexValue] 
                }
                dataToUpdate.push(row);
            }
        }

        // rows with fullmatches
        for (let i of selectedFullMatches.current) {
            dataToUpdate.push(fullMatchData[i])
        }

        // rows with nomatches
        for (let i of selectedNoMatches.current) {
            dataToInsert.push(noMatchData[i])
        }

        const postResponse = await postOPTData({post: dataToInsert, update: dataToUpdate});
        setMergeStatus(postResponse);
        setShowPopup(true);
    }

    const handlePopUpClose = () => {
        // Reset states
        setData([]);
        setMergeStatus({});
        setFetchedOPTData({});
        setFullMatchData([]);
        setPartialMatchData([]);
        setFullMatchData([]);
        setNoMatchData([]);
        setFile(null);

        setShowPopup(false);
    }

    const SuccessPopup = () => {
        return <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0, 0, 0, 0.35)",
                zIndex: 1000,
            }}
            >
            <div
                style={{
                    backgroundColor: "white",
                    padding: "30px 50px",
                    borderRadius: "12px",
                    boxShadow: "0 12px 20px rgba(0,0,0,0.35)",
                    width: "320px",
                    maxWidth: "80vw",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "20px",
                    textAlign: "center",
                }}
            >
                <h2 style={{ color: "#16a34a", margin: 0 }}>✅ Merge Completed!</h2>

                <div style={{ color: "#374151", fontSize: "16px", lineHeight: "1.5" }}>
                <p style={{ margin: "5px 0" }}>Updated Rows: {mergeStatus.updatedRows}</p>
                <p style={{ margin: "5px 0" }}>New Rows: {mergeStatus.newRows}</p>
                <p style={{ margin: "5px 0" }}>Unchanged Rows: {mergeStatus.unchanged}</p>
                </div>

                <button
                style={{
                    padding: "10px 20px",
                    borderRadius: "6px",
                    border: "none",
                    backgroundColor: "#2563eb",
                    color: "white",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#1d4ed8")}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#2563eb")}
                onClick={handlePopUpClose}
                >
                Okay
                </button>
            </div>
        </div>
    }

    const ErrorPopup = ({message}) => {
        return <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0, 0, 0, 0.35)",
                zIndex: 1000,
            }}
            >
            <div
                style={{
                    backgroundColor: "white",
                    padding: "30px 50px",
                    borderRadius: "12px",
                    boxShadow: "0 12px 20px rgba(0,0,0,0.35)",
                    width: "320px",
                    maxWidth: "80vw",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "20px",
                    textAlign: "center",
                }}
            >
                <h2 style={{ color: "#f42b2bff", margin: 0 }}>{message}</h2>

                <button
                style={{
                    padding: "10px 20px",
                    borderRadius: "6px",
                    border: "none",
                    backgroundColor: "#2563eb",
                    color: "white",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#1d4ed8")}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#2563eb")}
                onClick={() => setShowErrorPopup(false)}
                >
                Okay
                </button>
            </div>
        </div>
    }

    return (
    <div style={{display: "flex", justifyContent: "center", flexDirection: "column"}}>
        {/* Centralized Search Functionality */}
        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
          <label htmlFor="dashboardSearchInput" style={{ marginRight: '10px', fontSize: '16px', fontWeight: 'bold' }}>Location:</label>

          {/* Province Dropdown */}
          <select
            value={selectedProvince?.province_id || "Select Province"}
            onChange={(e) => {
                const provinceId = e.target.value;
                const selected = provinces.find(p => p.province_id === Number(provinceId));
                setSelectedProvince(selected ? selected : {});
            }}
            style={dropdownStyle}
          >
            <option value={0}>Select Province</option>
            {Array.isArray(provinces) && provinces.length > 0 && provinces.map((province) => (
              <option key={province.province_id} value={province.province_id}>
                {province.province_name}
              </option>
            ))}
          </select>

          {/* Municipal Dropdown */}
          <select
            value={Object.keys(selectedMunicipal).length !== 0 ? selectedMunicipal.municipality_id : "Select Municipality"}
            onChange={(e) => {
                const municipalityId = e.target.value;
                const selected = municipals.find(m => m.municipality_id === Number(municipalityId));
                setSelectedMunicipal(selected ? selected : {})
            }}
            style={dropdownStyle}
            disabled={Object.keys(selectedProvince).length === 0} // Disable if no province is selected
          >
            <option value={0}>Select Municipal</option>
            {Array.isArray(municipals) && municipals.length > 0 && municipals.map((municipal) => (
              <option key={municipal.municipality_id} value={municipal.municipality_id}>
                {municipal.municipality_name}
              </option>
            ))}
          </select>

          {/* Barangay Dropdown */}
          <select
            value={Object.keys(selectedBarangay).length !== 0 ? selectedBarangay.barangay_id : "Select Barangay"}
            onChange={(e) => {
              const barangayId = e.target.value;
              const selected = barangays.find(b => b.barangay_id === Number(barangayId));
              setSelectedBarangay(selected ? selected : {});
              // If user clears the search query after it was applied, reset the applied state
            //   if (isSearchApplied && e.target.value.trim() === '') {
            //     setIsSearchApplied(false);
            //     setSearchQuery('');
            //     setAppliedLocationData({}); // Clear applied location data
            //   }
            }}
            style={dropdownStyle}
            disabled={Object.keys(selectedMunicipal).length === 0} // Disable if no municipal is selected
          >
            <option value={0}>Select Barangay</option>
            {Array.isArray(barangays) && barangays.length > 0 && barangays.map((barangay) => (
              <option key={barangay.barangay_id} value={barangay.barangay_id}>
                {barangay.barangay_name}
              </option>
            ))}
          </select>

          <button
            onClick={handleApplySearch}
            style={{ ...buttonStyle, marginLeft: '10px', padding: '8px 15px', fontSize: '16px' }}
          >
            Set Location
          </button>
        </div>

        {/* Upload xlsx file container */}
        {Object.keys(appliedLocationData).length !== 0 && data.length === 0 && <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "start",
                minHeight: "100vh",
                backgroundColor: "#f9fafb",
                padding: "24px",
            }}
        >
            <div
                style={{
                    width: "100%",
                    maxWidth: "480px",
                    backgroundColor: "white",
                    borderRadius: "16px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    padding: "24px",
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "column"
                }}
            >
                <h2
                style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    marginBottom: "16px",
                    textAlign: "center",
                }}
                >
                    OPT Sheet for {appliedLocationData.barangayName}
                </h2>

                <label
                    htmlFor="file-upload"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px dashed #d1d5db",
                        borderRadius: "16px",
                        padding: "12px",
                        cursor: "pointer",
                        transition: "border-color 0.2s ease",
                    }}
                    onMouseEnter={(e) =>
                        (e.currentTarget.style.borderColor = "#3b82f6")
                    }
                    onMouseLeave={(e) =>
                        (e.currentTarget.style.borderColor = "#d1d5db")
                    }
                >
                    <span style={{ color: "#4b5563" }}>
                        Click or drag an .xlsx file here
                    </span>
                    <input
                        id="file-upload"
                        type="file"
                        accept=".xlsx"
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                    />
                </label>

                {file && (
                    <div>
                        <p
                            style={{
                            marginTop: "16px",
                            color: "#16a34a",
                            fontWeight: "500",
                            textAlign: "center"
                            }}
                        >
                            {file.name}
                        </p>
                    </div>
                )}
                {file && (
                    <button 
                        type="submit" 
                        style={{
                            backgroundColor: "#3b82f6",
                            color: "white",
                            padding: "10px 20px", 
                            border: "none", 
                            borderRadius: "6px", 
                            cursor: "pointer",
                            fontSize: "16px",
                            textAlign: "center",
                            display: "flex",
                            justifyContent: "center"
                        }}
                        onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "#6798e6ff")
                        }
                        onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor = "#3b82f6")
                        }
                        onClick={handleFile}
                        >
                            {!loading ? "Initiate Merge" : <CircularLoader />}
                        </button>
                )}
                {error && (
                <p
                    style={{
                    marginTop: "16px",
                    color: "#dc2626",
                    fontWeight: "500",
                    }}
                >
                    {error}
                </p>
                )}
            </div>
        </div>}

        {data.length !== 0 && <PartialMatchDataRows data={partialMatchData} type="Partial Matches"/>}
        {data.length !== 0 && <FullMatchDataRows data={noMatchData} type="No Matches"/>}
        {data.length !== 0 && <FullMatchDataRows data={fullMatchData} type="Full Matches"/>}
        {data.length !== 0 && <button onClick={handleMerge} style={{
                padding: "10px 20px",
                width: "10vw",
                margin: "10px auto",
                backgroundColor: "#2563eb", // modern blue
                color: "white",
                border: "none",
                borderRadius: "8px",
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "500",
                transition: "all 0.3s ease",
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#1d4ed8")}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#3a77fbff")}>Merge</button>}
        {showPopup && (<SuccessPopup />)}
        {showErrorPopup && (<ErrorPopup message={errorMessage}/>)}
    </div>
  )
}

export default OPTMerge